/**
 * Date boundaries for the dashboard's period figures.
 *
 * "Today" and "this month" mean the viewer's local day and month, not UTC's:
 * a shift that starts at 07:00 in India should see its own morning, so the
 * boundary is computed locally and then converted to the ISO instant the API
 * compares against.
 */

/** Midnight at the start of the local day, as an ISO instant. */
export function startOfToday(now: Date = new Date()): string {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  return start.toISOString();
}

/** Midnight at the start of the local month, as an ISO instant. */
export function startOfMonth(now: Date = new Date()): string {
  const start = new Date(now);
  start.setDate(1);
  start.setHours(0, 0, 0, 0);
  return start.toISOString();
}

/** End of the local day `days` from now, as an ISO instant. */
export function endOfDaysFromNow(days: number, now: Date = new Date()): string {
  const end = new Date(now);
  end.setDate(end.getDate() + days);
  end.setHours(23, 59, 59, 999);
  return end.toISOString();
}

/** Whole days elapsed since `iso`, never negative. Used to age a hold queue. */
export function daysSince(iso: string, now: Date = new Date()): number {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 0;
  return Math.max(0, Math.floor((now.getTime() - then.getTime()) / 86_400_000));
}

/** A short `YYYY-MM-DD` key for a local day, stable enough to use in query keys. */
export function dayKey(now: Date = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
